import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailPlus, RotateCw, Trash2, UserCog, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLE_LABELS = { admin: "Admin", editor: "Yazabilir", viewer: "İzleyici" };

export default function UsersPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [users, setUsers] = useState(null);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: u }, { data: inv }] = await Promise.all([
        api.get("/users"),
        api.get("/users/invites"),
      ]);
      setUsers(u);
      setInvites(inv);
    } catch (err) {
      toast.error(formatApiError(err));
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (user && user.role !== "admin") {
    return <Navigate to="/panel" replace />;
  }

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users/invite", form);
      toast.success(t("Davet gönderildi"));
      setOpen(false);
      setForm({ email: "", role: "viewer" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (inv) => {
    try {
      await api.post(`/users/invites/${inv.id}/resend`);
      toast.success(t("Davet yeniden gönderildi"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleCancelInvite = async (inv) => {
    try {
      await api.delete(`/users/invites/${inv.id}`);
      toast.success(t("Davet iptal edildi"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.email} silindi`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="fade-up" data-testid="users-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <UserCog className="h-7 w-7 text-blue-600" />
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">{t("Kullanıcılar")}</h1>
          </div>
          <p className="mt-2 text-slate-500">{t("Firmanızdaki kullanıcıları ve rollerini yönetin.")}</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-user-button"
          className="bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200 hover:-translate-y-[1px]">
          <MailPlus className="h-4 w-4 mr-2" />
          {t("Kullanıcı Davet Et")}
        </Button>
      </div>

      {invites.length > 0 && (
        <div className="mt-8 border border-amber-200 bg-amber-50/50 rounded-md p-5" data-testid="invites-section">
          <h2 className="font-display text-sm font-semibold text-[#0A1128] uppercase tracking-wider">
            {t("Bekleyen Davetler")}
          </h2>
          <div className="mt-3 space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-2.5"
                data-testid={`invite-row-${inv.email}`}>
                <span className="text-sm text-slate-700 flex-1 truncate">{inv.email}</span>
                <Badge variant="outline" className="border-blue-300 text-blue-700" data-testid={`invite-role-${inv.email}`}>
                  {t(ROLE_LABELS[inv.role] || inv.role)}
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => handleResend(inv)}
                  data-testid={`resend-invite-${inv.email}`}
                  className="text-slate-500 hover:text-blue-600 hover:bg-blue-50">
                  <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                  {t("Yeniden Gönder")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleCancelInvite(inv)}
                  data-testid={`cancel-invite-${inv.email}`}
                  className="text-slate-500 hover:text-red-600 hover:bg-red-50">
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  {t("İptal Et")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 border border-slate-200 bg-white rounded-md overflow-hidden">
        {users === null ? (
          <div className="p-12 flex justify-center" data-testid="users-loading">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t("Ad Soyad")}</TableHead>
                <TableHead>{t("E-posta")}</TableHead>
                <TableHead>{t("Rol")}</TableHead>
                <TableHead className="text-right">{t("İşlem")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-[#0A1128]">{u.name}</TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-blue-300 text-blue-700" data-testid={`user-role-${u.email}`}>
                      {t(ROLE_LABELS[u.role] || u.role)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {u.id !== user?.id && (
                      <Button variant="ghost" size="icon" aria-label="Sil"
                        onClick={() => handleDelete(u)}
                        data-testid={`delete-user-${u.email}`}
                        className="text-slate-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0A1128]">{t("Kullanıcı Davet Et")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {t("Kullanıcılarınızı e-posta ile davet edin. Davet bağlantısı 72 saat geçerlidir.")}
          </p>
          <form onSubmit={handleInvite} className="space-y-4" data-testid="user-form">
            <div className="space-y-2">
              <Label htmlFor="user-email">{t("E-posta")}</Label>
              <Input id="user-email" type="email" required value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="user-email-input" className="focus-visible:ring-blue-600" />
            </div>
            <div className="space-y-2">
              <Label>{t("Rol")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role-select" className="focus:ring-blue-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="user-role-admin">{t("Admin — tam yetki")}</SelectItem>
                  <SelectItem value="editor" data-testid="user-role-editor">{t("Yazabilir — kayıt ekler/düzenler")}</SelectItem>
                  <SelectItem value="viewer" data-testid="user-role-viewer">{t("İzleyici — sadece görür")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel-button">
                {t("Vazgeç")}
              </Button>
              <Button type="submit" disabled={saving} data-testid="user-save-button"
                className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Davet Gönder")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
